import React, { FC, useState } from "react";
import { Box, Typography, ActivityIndicator } from '@ttx/design-system';
import axios from 'axios';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';

interface SquareupCardCustomFormProps {
  transactionStatus: 'started' | 'loading' | 'success' | 'error';
  setTransactionStatus: (status: 'started' | 'loading' | 'success' | 'error') => void;
  amount: number;
  setTrackingId: (trackingId: string) => void;
  trackingId: string;
  description: string;
  receipt_email: string;
  metadata: Record<string, any>;
  onTxnSuccess?: () => void;
}

export const SquareupCardCustomForm: FC<SquareupCardCustomFormProps> = ({
  transactionStatus,
  setTransactionStatus,
  amount,
  setTrackingId,
  trackingId,
  description,
  receipt_email,
  metadata,
  onTxnSuccess,
}) => {
  const applicationId = process.env.NEXT_PUBLIC_SQUARE_APPLICATION_ID;
  const locationId = process.env.NEXT_PUBLIC_SQUARE_LOCATION_ID;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  React.useEffect(() => {
    if (!trackingId) return;
    const eventSource = new EventSource(
      `${process.env.NEXT_PUBLIC_SQUAREUP_BACKEND_URL}/sse/payment-events`
    );
    eventSource.onmessage = (event): void => {
      try {
        const { data } = JSON.parse(event.data);
        console.log('data', data,data.transactionId, trackingId, data.network);
        if (data.transactionId === trackingId && data.network === 'polymesh') {
          switch (data.status) {
            case 'success': {
              setTransactionStatus('success');
              if (onTxnSuccess) onTxnSuccess();
              break;
            }
            case 'failed': {
              setTransactionStatus('error');
              break;
            }
            default: break;
          }
        }
        else if (data.transactionId === trackingId && (data.network === 'ethereum' || data.network === 'polygon')) {
          switch (data.status) {
            case 'pending': {
              setTransactionStatus('success');
              break;
            }
            case 'failed': {
              setTransactionStatus('error');
              break;
            }
            default: break;
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error processing SSE message:', error);
      }
    };
    eventSource.onerror = (error) => {
      // eslint-disable-next-line no-console
      console.error('SSE Error:', error);
      eventSource.close();
    };
    return () => {
      eventSource.close();
    };
  }, [trackingId, onTxnSuccess, setTransactionStatus]);

  // Handle payment form submission via SDK
  const handleCardTokenize = async (token: any) => {
    setError(null);
    setLoading(true);
    setTransactionStatus('loading');
    try {
      const resp = await axios.post(
        `${process.env.NEXT_PUBLIC_SQUAREUP_BACKEND_URL}/squareup/intent`,
        {
          sourceId: token.token,
          amount,
          description,
          receipt_email,
          metadata,
        }
      );
      setTrackingId(resp.data.data.trackingId);
    } catch (err: any) {
      setError('Payment error: ' + (err.response?.data?.message || err.message));
      setTransactionStatus('error');
    } finally {
      setLoading(false);
    }
  };

  if (!applicationId || !locationId) {
    return (
      <Box p="eight" bg="surfaceBlack" borderRadius="two" width="100%" alignItems="center" justifyContent="center">
        <Typography textStyle="h3" color="brandPrimary">
          Missing Square Application ID or Location ID. Please check your environment configuration.
        </Typography>
      </Box>
    );
  }

  if (transactionStatus === 'loading') {
    return (
      <Box
        p="eight"
        bg="surfaceBlack"
        borderRadius="two"
        width="100%"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <ActivityIndicator color="brandPrimary" loaderColor="brandPrimary" size={60} />
        <Typography textStyle="h3" color="textDefault" style={{ marginTop: 16 }}>
          Processing Payment...
        </Typography>
      </Box>
    );
  }

  if (transactionStatus === 'success') {
    return null;
  }

  return (
    <Box bg="surfaceBlack" borderRadius="two" p="eight" style={{ width: '100%', maxWidth: 400, margin: 'auto' }}>
      {error && <Typography color="errorPrimary" style={{ marginBottom: 16 }}>{error}</Typography>}
      <PaymentForm
        applicationId={applicationId}
        locationId={locationId}
        cardTokenizeResponseReceived={handleCardTokenize}
      >
        <CreditCard
          buttonProps={{
            css: {
              backgroundColor: '#E6FF00',
              color: '#111',
              fontWeight: 400,
              fontSize: 16,
              borderRadius: 8,
              width: '100%',
              padding: '16px',
              marginTop: 8,
              boxShadow: '0 2px 8px 0 rgba(230,255,0,0.10)',
              '&:hover': {
                backgroundColor: '#d4e600',
              },
            },
          }}
          style={{
            input: {
              backgroundColor: '#111',
              color: '#fff',
              fontFamily: 'Arial',
              fontSize: '16px',
              fontWeight: 'normal',
            },
            'input::placeholder': {
              color: '#7a7b73',
            },
            'input.is-focus::placeholder': {
              color: '#3c3c40',
            },
            'input.is-focus': {
              backgroundColor: '#111',
              color: '#fff',
              fontFamily: 'Arial',
              fontSize: '16px',
              fontWeight: 'normal',
            },
            '.input-container': {
              borderColor: '#222',
              borderWidth: '1px',
              borderRadius: '8px',
            },
            '.input-container.is-focus': {
              borderColor: '#E6FF00',
              borderWidth: '2px',
            },
            '.input-container.is-error': {
              borderColor: '#EF4444',
              borderWidth: '2px',
            },
            '.message-text': {
              color: '#888',
            },
            '.message-icon': {
              color: '#E6FF00',
            },
            '@media screen and (max-width: 600px)': {
              input: {
                fontSize: '14px',
              },
            },
          }}
        />
      </PaymentForm>
    </Box>
  );
};